import { Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { Card } from '@deckplan.io/common/cards';
import { CardsAndTotalCount, CardService, QueryOptions } from '../card.service';
import { BehaviorSubject, merge, Observable, of } from 'rxjs';
import { AlertLevel, AlertsService } from '../../common/alerts/alerts.service';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Query, QueryGroup } from '@deckplan.io/common/filters';
import { PageChanges } from '../card-table/card-table.component';
import { SortDirection } from '@angular/material/sort';
import { InventoryCardsAndTotalCount, InventoryService } from '../../inventory/inventory.service';
import { InventoryCard } from '@deckplan.io/common/inventory';
import { EditableDataField } from '../../common/filter/editable-data-field';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Deck } from '@deckplan.io/common';
import { DecksService, DeckWithCardSizes } from '../../decks/decks.service';
import { AvailableColumn } from '../../common/simple-table/simple-table.component';
import { CommonColumns } from '../../decks/common/common-columns';

@Component({
    selector: 'app-card-search',
    templateUrl: './card-search.component.html',
    styleUrls: ['./card-search.component.scss']
})
export class CardSearchComponent implements OnInit {

    // The provided query will be AND'd with any
    // user defined queries
    @Input()
    public set setQuery(value: Query) {
        if (JSON.stringify(value) !== JSON.stringify(this.mSetQuery)) {
            this.mSetQuery = value;
            this.getCurrentPage().pipe(
                map((data: CardsAndTotalCount | InventoryCardsAndTotalCount) => this.loadCurrentPage(data)),
                catchError(error => this.handlePageLoadError(error))
            ).subscribe((cards: Card[]) => this.cards = cards);
        }
    }

    public get setQuery(): Query {
        return this.mSetQuery;
    }

    @Input() public overrideClick = false;

    @Output() public cardClicked: EventEmitter<Card | InventoryCard> = new EventEmitter<Card | InventoryCard>();
    @Output() public cardInputFieldChanged: EventEmitter<{ value: string; data: Card | InventoryCard; dataField: EditableDataField }> =
        new EventEmitter<{ value: string; data: Card | InventoryCard; dataField: EditableDataField }>();

    @Input() public columns: AvailableColumn[] = CommonColumns;
    @Input() public tableId = 'Common';
    @Input() public showDragHint = false;

    @Input() public searchInventory = false;
    @Input() public showOwnedCardsSelector = false;

    @Input() public deck: Deck;

    @Input() public removableCards: boolean | { deckId: number } = false;
    @Input() public addCardsToDeck: boolean | { deckId: number } = true;
    @Input() public addCardsToInventory = true;
    @Output() public cardActionTaken = new EventEmitter();

    @Input() public hideSearch = false;

    @Input() public userId: number;

    public cards: Card[] = [];

    public isLoadingResults = false;
    public resultsLength = 0;
    public defaultPageSize = 10;

    public readonly forceRequery = new BehaviorSubject<number>(1);

    private mSetQuery: Query = undefined;

    private readonly pageChanges = new BehaviorSubject<PageChanges>({
        sort: {
            active: 'name',
            direction: 'asc'
        },
        page: {
            pageIndex: 0,
            pageSize: 10
        }
    });

    private readonly queryChanges = new BehaviorSubject<Query>(null);

    constructor(private readonly cardService: CardService,
                private readonly deckService: DecksService,
                private readonly alertsService: AlertsService,
                private readonly inventoryService: InventoryService,
                private readonly injector: Injector) {
        const dialogData = this.injector.get(MAT_DIALOG_DATA, null);
        if (dialogData !== null) {
            // Calling setQuery too early can have side effects (like if we're supposed to be searching inventory, that won't be set
            // yet by this time in the constructor) which can result in errors. So, make sure that we actually have a query to search
            // before we just do so. For instance, the Card Picker has a query in the dialog data that it uses, but we don't want it
            // to make it all the way down here.
            if (dialogData.query) {
                this.setQuery = dialogData.query;
            }
            // Don't override the ownedCards selector setting unless we've been told to
            if (dialogData.showOwnedCardsSelector !== undefined) {
                this.showOwnedCardsSelector = dialogData.showOwnedCardsSelector;
            }
        }
    }

    @Input() public isValid = (card: Card) => true;

    public ngOnInit() {
        merge(
            this.forceRequery,
            this.pageChanges,
            this.queryChanges,
        ).pipe(
            startWith({}),
            switchMap(() => this.getCurrentPage()),
            map((data: CardsAndTotalCount | InventoryCardsAndTotalCount) => this.loadCurrentPage(data)),
            catchError(error => this.handlePageLoadError(error))
        ).subscribe((cards: Card[]) => this.cards = cards);
    }

    public pagingChanged(pageChanges: PageChanges) {
        this.pageChanges.next(pageChanges);
    }

    public queryChanged(newQuery: Query) {
        this.queryChanges.next(newQuery);
    }

    public onShowOwnedCardsSelectorChanged(event: MatSlideToggleChange) {
        this.searchInventory = event.checked;
        this.forceRequery.next(1);
    }

    private getCurrentPage(): Observable<CardsAndTotalCount | InventoryCardsAndTotalCount> {
        this.isLoadingResults = true;

        // Get the sort. We can't just pass in the MatSort/MatPaginator object as it has a circular reference
        // and causes a crash if we try to serialize it. As such let's strip off what we need.
        const {active, direction} = (this.pageChanges.value && this.pageChanges.value.sort) ? this.pageChanges.value.sort : {active: 'name', direction: 'asc' as SortDirection};
        const {pageIndex, pageSize} = (this.pageChanges.value && this.pageChanges.value.page) ? this.pageChanges.value.page : {pageIndex: 0, pageSize: this.defaultPageSize};

        let query = null;
        if (this.setQuery && this.queryChanges.value) {
            query = {
                operator: 'and',
                operands: [
                    this.setQuery,
                    this.queryChanges.value
                ]
            } as QueryGroup;
        } else if (this.setQuery) {
            query = this.setQuery;
        } else if (this.pageChanges.value) {
            query = this.queryChanges.value;
        }

        const queryOptions: QueryOptions = {
            pagination: {
                page: {pageIndex, pageSize},
                sort: {active, direction}
            },
            query
        };

        if (this.deck) {
            return this.deckService.get(this.deck.id, queryOptions.query, queryOptions.pagination as any).pipe(
                map((deck: DeckWithCardSizes) => ({
                    cards: deck.cards,
                    totalCount: deck.filteredCardCount
                } as CardsAndTotalCount))
            );
        } else if (!this.searchInventory) {
            return this.cardService.getCards(queryOptions);
        } else {
            return this.inventoryService.getInventory(queryOptions, this.userId);
        }
    }

    private loadCurrentPage(data: CardsAndTotalCount | InventoryCardsAndTotalCount): Card[] {
        this.isLoadingResults = false;
        this.resultsLength = data.totalCount;

        if ((data as any).cards) {
            return (data as CardsAndTotalCount).cards;
        } else {
            return (data as InventoryCardsAndTotalCount).inventoryCards;
        }
    }

    private handlePageLoadError(error) {
        console.error('ERROR: Could not get cards :/', error);
        this.alertsService.alert('We couldn\'t reach our servers :( Please try later...', AlertLevel.Critical);
        this.isLoadingResults = false;
        this.resultsLength = 0;
        return of([]);
    }
}
